import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Undo2,
  Check,
  X,
  ScanBarcode,
  ClipboardCheck,
  Package,
  Save,
  AlertCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DIALOG_TAB_TRIGGER,
  DialogEyebrow,
  SectionHeading,
} from '@/components/common/DialogPrimitives';
import { returnsService } from '@/services/returns';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ReturnOrder, ReturnStatus } from '@/types/returns';
import { ScanInwardDialog } from './ScanInwardDialog';

interface Props {
  record: ReturnOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_LABEL: Record<ReturnStatus, string> = {
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  RECEIVED: 'Received',
  QC_PENDING: 'QC pending',
  GOOD_INVENTORY: 'Good inventory',
  BAD_INVENTORY: 'Bad inventory',
  CLOSED: 'Closed',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

const STATUS_TONE: Record<ReturnStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  REQUESTED: 'warning',
  APPROVED: 'warning',
  RECEIVED: 'warning',
  QC_PENDING: 'warning',
  GOOD_INVENTORY: 'success',
  BAD_INVENTORY: 'muted',
  CLOSED: 'success',
  REJECTED: 'destructive',
  CANCELLED: 'muted',
};

export function ReturnDetailSheet({ record, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [scanOpen, setScanOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['returns', 'detail', record?.id],
    queryFn: () => returnsService.get(record!.id),
    enabled: !!record && open,
    initialData: record ?? undefined,
    staleTime: 5_000,
  });
  const current = detailQuery.data ?? record;

  const statusMutation = useMutation({
    mutationFn: ({ status, reason }: { status: ReturnStatus; reason?: string }) =>
      returnsService.updateStatus(current!.id, { status, reason }),
    onSuccess: (_d, vars) => {
      toast.success(`Return ${STATUS_LABEL[vars.status].toLowerCase()}`);
      qc.invalidateQueries({ queryKey: ['returns'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not update return'),
  });

  // Aftersales-3 — manual retry of the carrier reverse-pickup push.
  const retryCarrier = useMutation({
    mutationFn: () => returnsService.retryReversePickupCarrier(current!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['returns'] });
      toast.success('Carrier push retried');
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Carrier push failed'),
  });

  // Operator-initiated cancel. Frees up the SO line qty so the
  // customer can re-attempt.
  const [cancelOpen, setCancelOpen] = useState(false);
  const cancelMutation = useMutation({
    mutationFn: (reason: string) =>
      returnsService.cancel(current!.id, { reason }),
    onSuccess: () => {
      toast.success('Return cancelled');
      qc.invalidateQueries({ queryKey: ['returns'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setCancelOpen(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not cancel return'),
  });

  const status = current?.status ?? 'REQUESTED';
  const isTerminal = ['CLOSED', 'REJECTED', 'CANCELLED'].includes(status);
  const canApprove = status === 'REQUESTED';
  const canReject = ['REQUESTED', 'APPROVED'].includes(status);
  const canCancel = ['REQUESTED', 'APPROVED'].includes(status);
  const canMarkReceived = status === 'APPROVED';
  const canStartQc = status === 'RECEIVED';
  const canClose = ['QC_PENDING', 'GOOD_INVENTORY', 'BAD_INVENTORY'].includes(status);
  // Aftersales-4 — once a return reaches GOOD_INVENTORY / BAD_INVENTORY
  // the QC classification is final (the BE rejects re-running classify
  // — it would double-count stock + spawn a duplicate refund). Lock the
  // QcEditor so the "Save classification" button can't be clicked into
  // a guaranteed error.
  const classificationLocked =
    isTerminal || ['GOOD_INVENTORY', 'BAD_INVENTORY'].includes(status);

  // Aggregates for the stats strip and progress hint.
  const totals = useMemo(() => {
    const items = current?.items ?? [];
    return {
      claimed: items.reduce((s, l) => s + Number(l.quantity), 0),
      received: items.reduce((s, l) => s + Number(l.receivedQuantity), 0),
      good: items.reduce((s, l) => s + Number(l.goodQuantity), 0),
      bad: items.reduce((s, l) => s + Number(l.badQuantity), 0),
    };
  }, [current?.items]);

  const allReceived = totals.received >= totals.claimed && totals.claimed > 0;
  const allClassified =
    totals.received > 0 && totals.good + totals.bad >= totals.received;

  return (
    <TooltipProvider delayDuration={150}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex h-[88vh] w-full max-w-5xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-7 pt-6 pb-5">
            <DialogTitle className="sr-only">{current?.returnNumber ?? 'Return'}</DialogTitle>
            {current && (
              <div className="flex items-start gap-4">
                <span
                  aria-hidden="true"
                  className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-warning/10 to-warning/5 text-[hsl(38_85%_30%)] ring-1 ring-inset ring-warning/15"
                >
                  <Undo2 className="h-6 w-6" />
                </span>

                <div className="min-w-0 flex-1 space-y-1.5">
                  <DialogEyebrow>
                    Return · order {current.salesOrder.orderNumber} · {fmt.relative(current.createdAt)}
                  </DialogEyebrow>
                  <h2 className="font-mono text-[18px] font-semibold leading-tight tracking-tight text-foreground">
                    {current.returnNumber}
                  </h2>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant={STATUS_TONE[status]} className="text-[10px]">
                      {STATUS_LABEL[status]}
                    </Badge>
                    {current.resolutionType === 'EXCHANGE' && (
                      <Badge
                        variant="warning"
                        className="text-[10px]"
                        title="This return is settled by shipping a replacement item, not a refund"
                      >
                        Exchange
                      </Badge>
                    )}
                    {current.replacementOrderId && (
                      <Badge
                        variant="muted"
                        className="font-mono text-[10px]"
                        title="The replacement order auto-created at QC completion"
                      >
                        replacement EXG-{current.returnNumber}
                      </Badge>
                    )}
                    <Badge variant="muted" className="text-[10px]">
                      {current.customer?.name ?? '—'}
                    </Badge>
                    <Badge variant="muted" className="font-mono text-[10px]">
                      {current.customer?.code ?? '—'}
                    </Badge>
                    <Badge variant="muted" className="font-mono text-[10px]">
                      {current.warehouse?.code ?? '—'}
                    </Badge>
                    {current.externalReturnRef && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        ref {current.externalReturnRef}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {!isTerminal && (status === 'APPROVED' || status === 'RECEIVED') && (
                    <Button size="sm" onClick={() => setScanOpen(true)} variant="outline">
                      <ScanBarcode className="h-3.5 w-3.5" />
                      Scan inward
                    </Button>
                  )}
                  {canApprove && (
                    <Button
                      size="sm"
                      onClick={() => statusMutation.mutate({ status: 'APPROVED' })}
                      disabled={statusMutation.isPending}
                    >
                      <Check className="h-3.5 w-3.5" />
                      Approve
                    </Button>
                  )}
                  {canMarkReceived && (
                    <Button
                      size="sm"
                      onClick={() => statusMutation.mutate({ status: 'RECEIVED' })}
                      disabled={statusMutation.isPending}
                    >
                      Mark received
                    </Button>
                  )}
                  {canCancel && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCancelOpen(true)}
                      disabled={cancelMutation.isPending}
                    >
                      Cancel return
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogHeader>
          <Separator />

          {/* Stats strip */}
          <div className="flex items-stretch divide-x divide-border/60 border-b border-border/60 bg-muted/20 px-7">
            <Stat label="Claimed" value={fmt.number(totals.claimed)} accent />
            <Stat label="Received" value={fmt.number(totals.received)} />
            <Stat label="Good" value={fmt.number(totals.good)} />
            <Stat label="Bad" value={fmt.number(totals.bad)} muted />
            <Stat
              label="Outstanding"
              value={fmt.number(Math.max(0, totals.claimed - totals.received))}
              muted={totals.claimed - totals.received <= 0}
              accent={totals.claimed - totals.received > 0}
            />
          </div>

          <Tabs defaultValue="qc" className="flex flex-1 flex-col overflow-hidden">
            <div className="border-b border-border/60 bg-background px-7">
              <TabsList className="h-10 gap-2 bg-transparent p-0">
                <TabsTrigger value="qc" className={DIALOG_TAB_TRIGGER}>
                  <ClipboardCheck className="h-3.5 w-3.5" />
                  QC
                </TabsTrigger>
                <TabsTrigger value="lines" className={DIALOG_TAB_TRIGGER}>
                  <Package className="h-3.5 w-3.5" />
                  Lines
                </TabsTrigger>
                <TabsTrigger value="details" className={DIALOG_TAB_TRIGGER}>
                  Details
                </TabsTrigger>
              </TabsList>
            </div>

            <ScrollArea className="flex-1">
              <div className="px-7 py-5">
                {/* ── QC ──────────────────────────────────────────── */}
                <TabsContent value="qc" className="m-0 space-y-4">
                  <SectionHeading
                    title="Inspect & classify"
                    hint={
                      allClassified
                        ? 'All received units classified'
                        : allReceived
                          ? 'Ready for QC — split each line into good / bad'
                          : 'Awaiting more units to arrive'
                    }
                    action={
                      !isTerminal && (status === 'APPROVED' || status === 'RECEIVED') && (
                        <Button size="sm" variant="outline" onClick={() => setScanOpen(true)}>
                          <ScanBarcode className="h-3.5 w-3.5" />
                          Scan inward
                        </Button>
                      )
                    }
                  />

                  {!current || current.items.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border/70 px-4 py-10 text-center text-[12.5px] text-muted-foreground">
                      No return lines.
                    </div>
                  ) : (
                    <QcEditor record={current} disabled={classificationLocked} />
                  )}

                  {!isTerminal && (
                    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border/60 pt-4">
                      {canStartQc && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => statusMutation.mutate({ status: 'QC_PENDING' })}
                          disabled={statusMutation.isPending}
                        >
                          Mark QC in progress
                        </Button>
                      )}
                      {canClose && allClassified && (
                        <Button
                          size="sm"
                          onClick={() => statusMutation.mutate({ status: 'CLOSED' })}
                          disabled={statusMutation.isPending}
                        >
                          <Check className="h-3.5 w-3.5" />
                          Close return
                        </Button>
                      )}
                      {canReject && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            statusMutation.mutate({
                              status: 'REJECTED',
                              reason: 'Rejected by operator',
                            })
                          }
                          disabled={statusMutation.isPending}
                          className="border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive"
                        >
                          <X className="h-3.5 w-3.5" />
                          Reject return
                        </Button>
                      )}
                    </div>
                  )}
                </TabsContent>

                {/* ── Lines (read-only summary) ───────────────────── */}
                <TabsContent value="lines" className="m-0 space-y-3">
                  <SectionHeading
                    title="Line items"
                    hint={`${current?.items.length ?? 0} line${current?.items.length === 1 ? '' : 's'}`}
                  />
                  {!current || current.items.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border/70 px-4 py-10 text-center text-[12.5px] text-muted-foreground">
                      No lines.
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-lg border border-border/70">
                      <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                        <div className="col-span-5">Item</div>
                        <div className="col-span-2 text-right">Claimed</div>
                        <div className="col-span-2 text-right">Received</div>
                        <div className="col-span-1 text-right">Good</div>
                        <div className="col-span-1 text-right">Bad</div>
                        <div className="col-span-1" />
                      </div>
                      {current.items.map((l) => (
                        <div
                          key={l.id}
                          className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2.5 text-[12.5px] last:border-0"
                        >
                          <div className="col-span-5 min-w-0">
                            <div className="truncate font-medium text-foreground">{l.name}</div>
                            <div className="font-mono text-[10.5px] text-muted-foreground">{l.sku}</div>
                          </div>
                          <div className="col-span-2 text-right font-mono tabular-nums">
                            {fmt.number(Number(l.quantity))}
                          </div>
                          <div className="col-span-2 text-right font-mono tabular-nums">
                            {fmt.number(Number(l.receivedQuantity))}
                          </div>
                          <div className="col-span-1 text-right font-mono tabular-nums text-success">
                            {fmt.number(Number(l.goodQuantity))}
                          </div>
                          <div className="col-span-1 text-right font-mono tabular-nums text-destructive">
                            {fmt.number(Number(l.badQuantity))}
                          </div>
                          <div className="col-span-1 text-right">
                            {l.classification && (
                              <Badge variant="muted" className="text-[10px]">
                                {l.classification === 'GOOD_INVENTORY' ? 'good' : 'bad'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* ── Details ─────────────────────────────────────── */}
                <TabsContent value="details" className="m-0 space-y-5">
                  <SectionHeading title="Return metadata" />
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    <DetailRow label="Reason" value={current?.reason ?? '—'} />
                    <DetailRow label="Created" value={fmt.dateTime(current?.createdAt)} />
                    <DetailRow
                      label="Received"
                      value={current?.receivedAt ? fmt.dateTime(current.receivedAt) : '—'}
                    />
                    <DetailRow
                      label="Closed"
                      value={current?.closedAt ? fmt.dateTime(current.closedAt) : '—'}
                    />
                    <DetailRow label="Warehouse" value={current?.warehouse?.name ?? '—'} />
                    <DetailRow label="Customer" value={current?.customer.name ?? '—'} />
                  </div>

                  {/* Reverse-pickup tracking — visible once approve has spawned the pickup */}
                  {current?.reversePickups && current.reversePickups.length > 0 && (
                    <>
                      <Separator />
                      <section className="space-y-3">
                        <SectionHeading title="Reverse pickup" />
                        <div className="space-y-2">
                          {current.reversePickups.map((rp) => (
                            <div
                              key={rp.id}
                              className="space-y-2 rounded-lg border border-border/60 bg-muted/10 p-3"
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="space-y-0.5">
                                  <div className="font-mono text-[12px] font-medium text-foreground">
                                    {rp.dispatchNumber}
                                  </div>
                                  {rp.partner && (
                                    <div className="text-[11.5px] text-muted-foreground">
                                      via {rp.partner.name}
                                      <span className="ml-1 font-mono text-[10.5px] text-muted-foreground/70">
                                        ({rp.partner.code})
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <Badge variant="muted" className="text-[10px]">{rp.status}</Badge>
                              </div>
                              <div className="grid grid-cols-2 gap-3 text-[11.5px] sm:grid-cols-4">
                                <div>
                                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Tracking</div>
                                  <div className="font-mono">{rp.trackingNumber ?? '—'}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Picked at</div>
                                  <div className="font-mono tabular-nums">
                                    {rp.pickedAt ? fmt.dateTime(rp.pickedAt) : '—'}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">ETA to warehouse</div>
                                  <div className="font-mono tabular-nums">
                                    {rp.expectedDeliveryAt ? fmt.dateTime(rp.expectedDeliveryAt) : '—'}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Delivered at</div>
                                  <div className="font-mono tabular-nums">
                                    {rp.deliveredAt ? fmt.dateTime(rp.deliveredAt) : '—'}
                                  </div>
                                </div>
                              </div>
                              {/* Aftersales-3 — carrier push state */}
                              {rp.trackingNumber ? (
                                <div className="rounded border border-emerald-500/25 bg-emerald-500/[0.06] px-2 py-1.5 text-[10.5px] text-emerald-700">
                                  Pushed to carrier — AWB {rp.trackingNumber} assigned. Once the
                                  partner marks this pickup DELIVERED, the return auto-transitions
                                  to RECEIVED for warehouse scan-inward.
                                </div>
                              ) : rp.failureReason ? (
                                <div className="space-y-1.5 rounded border border-destructive/30 bg-destructive/[0.05] px-2 py-1.5">
                                  <div className="text-[10.5px] text-destructive">
                                    Carrier push failed: {rp.failureReason}
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => retryCarrier.mutate()}
                                    disabled={retryCarrier.isPending}
                                  >
                                    {retryCarrier.isPending ? 'Retrying…' : 'Push to carrier'}
                                  </Button>
                                </div>
                              ) : (
                                <div className="space-y-1.5 rounded border border-border/50 bg-muted/20 px-2 py-1.5">
                                  <div className="text-[10.5px] text-muted-foreground">
                                    Not yet pushed to the carrier. The partner either doesn't
                                    support automated reverse pickups, or the auto-push hasn't
                                    completed — drive the pickup manually, or retry the carrier push.
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => retryCarrier.mutate()}
                                    disabled={retryCarrier.isPending}
                                  >
                                    {retryCarrier.isPending ? 'Pushing…' : 'Push to carrier'}
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </section>
                    </>
                  )}
                  {/* Aftersales-4 — QC-driven refund(s) */}
                  {current?.refunds && current.refunds.length > 0 && (
                    <>
                      <Separator />
                      <section className="space-y-3">
                        <SectionHeading title="Refund" />
                        <div className="space-y-2">
                          {current.refunds.map((rf) => (
                            <div
                              key={rf.id}
                              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 bg-muted/10 p-3"
                            >
                              <div className="space-y-0.5">
                                <div className="font-mono text-[12px] font-medium text-foreground">
                                  {rf.refundNumber}
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                  {rf.type} · auto-created from QC
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-mono text-[14px] tabular-nums text-foreground">
                                  {fmt.money(Number(rf.amount), rf.currency)}
                                </div>
                                <Badge variant="muted" className="mt-0.5 text-[10px]">
                                  {rf.status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10.5px] text-muted-foreground">
                          Eligible amount = good-classified units × original unit price.
                          The portal drives the actual gateway refund and confirms back.
                        </p>
                      </section>
                    </>
                  )}
                  {current?.notes && (
                    <>
                      <Separator />
                      <section className="space-y-2">
                        <SectionHeading title="Notes" />
                        <p className="text-[13px] leading-relaxed text-foreground">{current.notes}</p>
                      </section>
                    </>
                  )}
                </TabsContent>
              </div>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>

      <ScanInwardDialog
        record={current}
        open={scanOpen}
        onOpenChange={setScanOpen}
      />

      <CancelReturnDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onConfirm={(reason) => cancelMutation.mutate(reason)}
        pending={cancelMutation.isPending}
      />
    </TooltipProvider>
  );
}

function CancelReturnDialog({
  open,
  onOpenChange,
  onConfirm,
  pending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  pending: boolean;
}) {
  const [reason, setReason] = useState('');

  // Reset on close so a re-open starts blank.
  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel return</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[13px]">
          <p className="text-muted-foreground">
            Cancelling closes this return without a refund or exchange. The
            quantity becomes available again so the customer can raise a fresh
            return on the same order if needed.
          </p>
          <div className="space-y-1">
            <label className="text-[11.5px] font-medium text-foreground">
              Reason <span className="text-destructive">*</span>
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Customer changed their mind / Carrier cancelled pickup"
              autoFocus
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Keep return
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(reason.trim())}
            disabled={pending || !reason.trim()}
          >
            {pending ? 'Cancelling…' : 'Cancel return'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── QC editor ──────────────────────────────────────────────────────────

interface QcDraftLine {
  itemId: string;
  good: string;
  bad: string;
}

function QcEditor({ record, disabled }: { record: ReturnOrder; disabled: boolean }) {
  const qc = useQueryClient();
  const [lines, setLines] = useState<QcDraftLine[]>([]);

  // Hydrate from canonical record on mount / when items change.
  useEffect(() => {
    setLines(
      record.items.map((l) => ({
        itemId: l.id,
        good: String(Number(l.goodQuantity) || ''),
        bad: String(Number(l.badQuantity) || ''),
      })),
    );
  }, [record.items]);

  const classify = useMutation({
    mutationFn: () =>
      returnsService.classify(record.id, {
        lines: lines
          .map((l) => ({
            returnOrderItemId: l.itemId,
            goodQuantity: l.good ? Number(l.good) : undefined,
            badQuantity: l.bad ? Number(l.bad) : undefined,
          }))
          .filter((l) => l.goodQuantity != null || l.badQuantity != null),
      }),
    onSuccess: () => {
      toast.success('Classification saved');
      qc.invalidateQueries({ queryKey: ['returns'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not classify lines'),
  });

  const update = (itemId: string, field: 'good' | 'bad', value: string) =>
    setLines((ls) => ls.map((l) => (l.itemId === itemId ? { ...l, [field]: value } : l)));

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-lg border border-border/70">
        <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          <div className="col-span-5">Item</div>
          <div className="col-span-2 text-right">Received</div>
          <div className="col-span-2 text-right">Good</div>
          <div className="col-span-2 text-right">Bad</div>
          <div className="col-span-1 text-right">Δ</div>
        </div>
        {record.items.map((line) => {
          const draft = lines.find((d) => d.itemId === line.id);
          const goodN = Number(draft?.good ?? 0) || 0;
          const badN = Number(draft?.bad ?? 0) || 0;
          const received = Number(line.receivedQuantity);
          const remaining = received - goodN - badN;
          const overflow = goodN + badN > received;

          return (
            <div
              key={line.id}
              className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2.5 text-[12.5px] last:border-0"
            >
              <div className="col-span-5 min-w-0">
                <div className="truncate font-medium text-foreground">{line.name}</div>
                <div className="font-mono text-[10.5px] text-muted-foreground">{line.sku}</div>
              </div>
              <div className="col-span-2 text-right font-mono tabular-nums text-foreground">
                {fmt.number(received)}
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.001"
                  value={draft?.good ?? ''}
                  onChange={(e) => update(line.id, 'good', e.target.value)}
                  placeholder="0"
                  className="h-7 text-right font-mono text-[12px] tabular-nums"
                  disabled={disabled || received <= 0}
                />
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.001"
                  value={draft?.bad ?? ''}
                  onChange={(e) => update(line.id, 'bad', e.target.value)}
                  placeholder="0"
                  className="h-7 text-right font-mono text-[12px] tabular-nums"
                  disabled={disabled || received <= 0}
                />
              </div>
              <div
                className={cn(
                  'col-span-1 text-right font-mono text-[11.5px] tabular-nums',
                  overflow
                    ? 'text-destructive'
                    : remaining > 0
                      ? 'text-muted-foreground'
                      : 'text-success',
                )}
              >
                {overflow ? `+${fmt.number(goodN + badN - received)}` : fmt.number(remaining)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
          {disabled ? (
            <span className="inline-flex items-center gap-1.5 text-success">
              <Check className="h-3 w-3" />
              Classification complete — this return is locked.
            </span>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center gap-1.5">
                  <AlertCircle className="h-3 w-3" />
                  Sum of good + bad cannot exceed received qty per line.
                </span>
              </TooltipTrigger>
              <TooltipContent>BE rejects classifications that overflow received qty</TooltipContent>
            </Tooltip>
          )}
        </div>
        {!disabled && (
          <Button
            size="sm"
            onClick={() => classify.mutate()}
            disabled={classify.isPending}
          >
            <Save className="h-3.5 w-3.5" />
            {classify.isPending ? 'Saving…' : 'Save classification'}
          </Button>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-[13px] text-foreground">{value}</div>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  muted,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex-1 min-w-[110px] px-4 py-3.5 first:pl-0 last:pr-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-1.5 font-mono text-[19px] leading-none tabular-nums',
          accent ? 'font-semibold text-foreground' : muted ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}
